/**
 * The course focus (UX_EXPERIENCE §4.3a, #442): which selected course Homework and Progress show.
 *
 * - ~~**Chosen by hand, never inferred.** Nothing here reads a date, a due list or an exam; the only inputs are the
 *   learner's own choice and the list of selected courses.~~ *Amended 2026-11-03 (#641):* opening a homework or its
 *   feedback is the learner choosing that course, so the focus follows it (once; a later change by hand holds). Still
 *   never read from a date, a due list or an exam.
 * - **Persists until changed**, as a per-viewer convenience in localStorage keyed by learner id. It is not learner
 *   data, and every read and write is guarded (storage may be blocked).
 * - **Default**: nothing stored, or the stored course is no longer selected, gives the first selected course.
 * - **Changes what is shown, never what is recorded**: the focus is never sent with any request.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CourseResponse } from '@davinci/contracts';
import { queryKeys } from '../api/query-keys.js';
import { useApi } from './api-context.js';

export type FocusCourse = CourseResponse;

const STORAGE_PREFIX = 'davinci.courseFocus.';

function readStored(learnerId: string): string | null {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + learnerId);
  } catch {
    return null;
  }
}

function writeStored(learnerId: string, courseId: string): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + learnerId, courseId);
  } catch {
    // Storage may be blocked; the choice then lasts for the session only.
  }
}

export interface CourseFocusValue {
  /** Every selected course, in the server's order. */
  readonly selectedCourses: readonly FocusCourse[];
  readonly focused: FocusCourse | null;
  readonly setFocus: (moodleCourseId: string) => void;
}

const CourseFocusContext = createContext<CourseFocusValue>({
  selectedCourses: [],
  focused: null,
  setFocus: () => undefined,
});

export function CourseFocusProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const api = useApi();
  const profile = useQuery({ queryKey: queryKeys.profile, queryFn: () => api.profile(), retry: false });
  const courses = useQuery({ queryKey: queryKeys.courses, queryFn: () => api.getCourses() });
  const learnerId = profile.data?.learnerId ?? null;
  const [chosen, setChosen] = useState<{ readonly learnerId: string; readonly id: string } | null>(null);

  const selectedCourses = useMemo(() => (courses.data?.courses ?? []).filter((c) => c.selected), [courses.data]);
  const storedId = useMemo(
    () =>
      chosen !== null && chosen.learnerId === learnerId
        ? chosen.id
        : learnerId === null
          ? null
          : readStored(learnerId),
    [chosen, learnerId],
  );
  const focused = selectedCourses.find((c) => c.moodleCourseId === storedId) ?? selectedCourses[0] ?? null;

  const setFocus = useCallback(
    (moodleCourseId: string) => {
      if (learnerId === null) return;
      writeStored(learnerId, moodleCourseId);
      setChosen({ learnerId, id: moodleCourseId });
    },
    [learnerId],
  );

  const value = useMemo<CourseFocusValue>(
    () => ({ selectedCourses, focused, setFocus }),
    [selectedCourses, focused, setFocus],
  );
  return <CourseFocusContext.Provider value={value}>{children}</CourseFocusContext.Provider>;
}

export function useCourseFocus(): CourseFocusValue {
  return useContext(CourseFocusContext);
}

/**
 * Opening a homework or its feedback is the learner choosing that course (#641): move the focus to `courseRef` once per
 * distinct value, so a later change by hand on the same page holds. A course that is not selected does nothing.
 */
export function useFocusFollows(courseRef: string | undefined): void {
  const { selectedCourses, focused, setFocus } = useCourseFocus();
  const followed = useRef<string | undefined>(undefined);
  const focusedId = focused?.moodleCourseId;
  useEffect(() => {
    if (courseRef === undefined || followed.current === courseRef) return;
    if (!selectedCourses.some((c) => c.moodleCourseId === courseRef)) return;
    followed.current = courseRef;
    if (courseRef !== focusedId) setFocus(courseRef);
  }, [courseRef, selectedCourses, focusedId, setFocus]);
}

/** Bidi embedding, override and isolate controls: a title carrying one could end the FSI ... PDI isolate early. */
const BIDI_CONTROLS = /[‪-‮⁦-⁩]/g;

/** Plain text for an `<option>` or `title`, isolated so a mixed-direction course title cannot reorder its neighbours. */
export function isolate(text: string): string {
  return `⁨${text.replace(BIDI_CONTROLS, '')}⁩`;
}

/** The course colour as a swatch. Decorative: the code and title always sit beside it, and it never encodes status. */
export function CourseSwatch({ colour }: { readonly colour: FocusCourse['colour'] }): React.JSX.Element {
  return <span className="course-swatch" aria-hidden="true" style={{ background: `var(--colour-course-${colour})` }} />;
}
